import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { WarningCircle, ArrowsClockwise, XCircle } from '@phosphor-icons/react';

// Single user duplicate
export function SingleDuplicateDialog({ open, onOpenChange, existingUser, onUpdate, onCancel }) {
  if (!existingUser) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#2B4B8C] flex items-center gap-2">
            <WarningCircle size={22} className="text-amber-500" /> Usuario ya existe
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#2B4B8C]/70">
            Ya existe un registro con estos datos:
          </p>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-1 text-sm">
            <p><span className="text-[#2B4B8C]/50">Nombre:</span> <strong className="text-[#2B4B8C]">{existingUser.first_name} {existingUser.last_name}</strong></p>
            <p><span className="text-[#2B4B8C]/50">RUT:</span> <strong className="text-[#2B4B8C]">{existingUser.rut || '-'}</strong></p>
            <p><span className="text-[#2B4B8C]/50">Email:</span> <strong className="text-[#2B4B8C]">{existingUser.email}</strong></p>
            <p><span className="text-[#2B4B8C]/50">Cargo:</span> <strong className="text-[#2B4B8C]">{existingUser.cargo || '-'}</strong></p>
            <p><span className="text-[#2B4B8C]/50">Teléfono:</span> <strong className="text-[#2B4B8C]">{existingUser.phone || '-'}</strong></p>
            {existingUser.company_name && <p><span className="text-[#2B4B8C]/50">Empresa:</span> <strong className="text-[#2B4B8C]">{existingUser.company_name}</strong></p>}
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={onUpdate} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="update-existing-btn">
              <ArrowsClockwise size={16} className="mr-1.5" /> Actualizar datos existentes
            </Button>
            <Button onClick={onCancel} variant="outline" className="border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="cancel-duplicate-btn">
              <XCircle size={16} className="mr-1.5" /> Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Bulk duplicate dialog
export function BulkDuplicateDialog({ open, onOpenChange, duplicates, total, onUpdateExisting, onSkipExisting, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#2B4B8C] flex items-center gap-2">
            <WarningCircle size={22} className="text-amber-500" /> Registros duplicados detectados
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#2B4B8C]/70">
            Se encontraron <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 border mx-1">{duplicates.length}</Badge> de {total} registros que ya existen en el sistema.
          </p>
          <div className="max-h-48 overflow-y-auto rounded-lg border border-[#2B4B8C]/10">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                  <TableHead className="text-[#2B4B8C]/60 text-xs">Fila</TableHead>
                  <TableHead className="text-[#2B4B8C]/60 text-xs">Nombre Existente</TableHead>
                  <TableHead className="text-[#2B4B8C]/60 text-xs">RUT</TableHead>
                  <TableHead className="text-[#2B4B8C]/60 text-xs">Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {duplicates.map((d, i) => (
                  <TableRow key={i} className="border-[#2B4B8C]/5">
                    <TableCell className="text-[#2B4B8C]/60 text-sm py-1.5">{d.row}</TableCell>
                    <TableCell className="text-[#2B4B8C] text-sm font-medium py-1.5">{d.existing.first_name} {d.existing.last_name}</TableCell>
                    <TableCell className="text-[#2B4B8C]/60 text-sm py-1.5">{d.existing.rut || '-'}</TableCell>
                    <TableCell className="text-[#2B4B8C]/60 text-sm py-1.5">{d.existing.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={onUpdateExisting} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="bulk-update-existing-btn">
              <ArrowsClockwise size={16} className="mr-1.5" /> Actualizar existentes y crear nuevos ({total - duplicates.length} nuevos, {duplicates.length} actualizados)
            </Button>
            <Button onClick={onSkipExisting} variant="outline" className="border-[#2BBAA4]/30 text-[#2BBAA4]" data-testid="bulk-skip-existing-btn">
              Solo crear nuevos ({total - duplicates.length} nuevos, {duplicates.length} omitidos)
            </Button>
            <Button onClick={onCancel} variant="ghost" className="text-[#2B4B8C]/50" data-testid="bulk-cancel-duplicate-btn">
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
